import { STATUS_SEARCH } from './_constants';

/**
 * Minimum payload object type for creating new agreements. Additional properties are supported though untracked.
 * @typedef {Object} AgreementInfo
 * @property {Object[]} fileInfos
 * @property {string} fileInfos[].libraryDocumentId
 * @property {string} name
 * @property {Object[]} mergeFieldInfo
 * @property {string} mergeFieldInfo[].defaultValue
 * @property {string} mergeFieldInfo[].fieldName
 * @property {Object[]} participantSetsInfo
 * @property {Object[]} participantSetsInfo[].memberInfos
 * @property {string} participantSetsInfo[].memberInfos[].email
 * @property {number} participantSetsInfo[].order
 * @property {string} participantSetsInfo[].role
 * @property {string} signatureType
 * @property {string} state
 * @property {Object[]} ccs
 * @property {string} ccs[].email
 * @property {Object} externalId
 * @property {string} externalId.Id
 * @property {string} groupId
 * @property {string} message
 * @property {string} type
 */

/**
 * Minimum payload object type for creating new agreement reminders. Additional properties are supported though untracked.
 * @typedef {Object} ReminderInfo
 * @property {number} firstReminderDelay
 * @property {string} frequency
 * @property {string} note
 * @property {string[]} recipientParticipantIds
 * @property {string} status
 */

/**
 * Minimum payload object type for updating outstanding agreement participant set. Additional properties are supported though untracked.
 * @typedef {Object} DetailedParticipantSetInfo
 * @property {Object[]} memberInfos
 * @property {string} memberInfos[].email
 * @property {string} memberInfos[].id
 * @property {Object} memberInfos[].securityOption
 * @property {string} memberInfos[].securityOption.authenticationMethod
 * @property {string} order
 * @property {string} role
 * @property {string} status
 */

/**
 * Minimum payload object type for updating agreement state. Additional properties are supported though untracked.
 * @typedef {Object} AgreementStateInfo
 * @property {string} state
 * @property {Object} agreementCancellationInfo
 * @property {string} agreementCancellationInfo.comment
 * @property {boolean} agreementCancellationInfo.notifyOthers
 */

/**
 * Minimum payload object type for searching agreement assets. Additional properties are supported though untracked.
 * @typedef {Object} SearchBody
 * @property {string} scope=AGREEMENT_ASSETS
 * @property {Object[]=} agreementAssetsCriteria
 * @property {string=} query
 */

/**
 * HTTP request payload for creating a new agreement and initiating its signing workflow.
 * @type {AgreementInfo}
 */
export const POST_AGREEMENT = {
	fileInfos: [
		{
			libraryDocumentId: '',
		},
	],
	name: '',
	mergeFieldInfo: [
		{
			defaultValue: '',
			fieldName: 'Third-Party Organization Name',
		},
	],
	participantSetsInfo: [
		{
			memberInfos: [
				{
					email: '',
				},
			],
			order: 1,
			role: 'SIGNER',
		},
	],
	signatureType: 'ESIGN',
	state: 'IN_PROCESS',
	ccs: [
		{
			email: '',
		},
	],
	externalId: {
		id: '',
	},
	groupId: '',
	message:
		'Your signature has been requested on a UPMC third-party organization agreement for ',
	type: 'AGREEMENT',
};

/**
 * HTTP request payload for initiating reminder communications on an outstanding agreement.
 * @type {ReminderInfo}
 */
export const POST_REMINDER = {
	firstReminderDelay: null,
	frequency: 'ONCE',
	note: 'Your signature has been requested on this UPMC third-party organization agreement.',
	recipientParticipantIds: [],
	status: 'ACTIVE',
};

/**
 * HTTP request payload for updating participant set of an outstanding agreement.
 * @type {DetailedParticipantSetInfo}
 */
export const PUT_PARTICIPANT_SET = {
	memberInfos: [
		{
			email: '',
			id: '',
			securityOption: {
				authenticationMethod: '',
			},
		},
	],
	order: '1',
	role: 'SIGNER',
	status: 'WAITING_FOR_MY_SIGNATURE',
};

/**
 * HTTP request payload for updating state of an agreement.
 * @type {AgreementStateInfo}
 */
export const PUT_STATE = {
	state: '',
	agreementCancellationInfo: {
		comment: '',
		notifyOthers: true,
	},
};

/**
 * HTTP request payload for searching agreement assets. Defaults to searching non-completed assets.
 * @type {SearchBody}
 */
export const POST_SEARCH = {
	scope: 'AGREEMENT_ASSETS',
	agreementAssetsCriteria: {
		status: Object.keys(STATUS_SEARCH)
			.map(key => STATUS_SEARCH[key])
			.filter(
				status =>
					![
						STATUS_SEARCH.ARCHIVED,
						STATUS_SEARCH.CANCELLED,
						STATUS_SEARCH.DISABLED,
						STATUS_SEARCH.EXPIRED,
						STATUS_SEARCH.OTHER,
						STATUS_SEARCH.REMOVED,
						STATUS_SEARCH.SIGNED,
					].includes(status)
			),
		type: ['AGREEMENT'],
	},
	query: '',
};
